/**
 * Public Membership / Pricing Page
 * Shareable at /pricing – same plan data as protected MembershipPage, minimal layout (logo only).
 * No auth, no payment; CTA to sign up.
 *
 * @author Varun Pandey
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Check, ExternalLink, GitCompare, Image, Video, Rocket } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { PublicLayout } from '@/components/layout/PublicLayout';
import {
  getPlanDisplayPrice,
  MEMBERSHIP_PLANS,
  type Plan,
} from '@/data/membershipPlansData';

type BillingPeriod = 'quarterly' | 'annually';

export const PublicMembershipPage: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');

  const getPrice = (plan: Plan) =>
    getPlanDisplayPrice(
      plan,
      billingPeriod === 'annually' ? 'annually' : 'quarterly',
    );
  const getSavings = (plan: Plan) =>
    billingPeriod === 'annually' ? plan.annualSavings : plan.quarterlySavings;
  const getEmiAmount = (plan: Plan): number | null => {
    if (billingPeriod === 'quarterly') {
      if (plan.id === 'economy') return 109;
      if (plan.id === 'premium') return 105;
      if (plan.id === 'elite') return 264;
    }
    if (billingPeriod === 'annually') {
      if (plan.id === 'economy') return 105;
      if (plan.id === 'premium') return 281;
      if (plan.id === 'elite') return 703;
    }
    return null;
  };

  return (
    <PublicLayout>
      <div className="w-full min-h-screen bg-[#0B0B0D] text-white relative overflow-hidden">
        {/* Subtle ambient gradient – premium depth without distracting */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[min(100%,720px)] h-96 bg-gradient-to-b from-[#F33363]/[0.06] via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute top-20 right-0 w-80 h-80 bg-[#1C1C1E]/40 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-[90vw] md:max-w-[95vw] mx-auto md:px-4 py-12 md:py-16 lg:py-20 pb-12">
          {/* Header – premium typography & hierarchy */}
          <header className="mb-10 lg:mb-14">
            <p className="text-[#636366] text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              Pricing
            </p>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="rounded-2xl p-2 bg-[#1C1C1E]/80 border border-[#2C2C2E]">
                <Star className="h-6 w-6 lg:h-7 lg:w-7 text-[#8E8E93]" strokeWidth={1.5} />
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-[-0.03em] leading-[1.1]">
                <span className="bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-[#A1A1A6]">
                  Membership
                </span>
              </h1>
            </div>
            <div className="h-px w-16 bg-gradient-to-r from-[#F33363]/60 to-transparent rounded-full mb-5" />
            <p className="text-[#8E8E93] text-base lg:text-lg font-medium max-w-xl leading-relaxed">
              Choose a plan that fits your goals. Built for artists and creators who want a genuine, professional presence.
            </p>
          </header>

          {/* Billing Period Tabs + Compare plans – same row */}
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4 border-b border-[#3A3A3C] pb-0">
            <nav className="flex gap-6 lg:gap-8" aria-label="Billing period">
              <button
                type="button"
                onClick={() => setBillingPeriod('quarterly')}
                className={cn(
                  'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                  billingPeriod === 'quarterly' ? 'text-white' : 'text-[#8E8E93] hover:text-white'
                )}
                aria-current={billingPeriod === 'quarterly' ? 'page' : undefined}
              >
                Quarterly
                {billingPeriod === 'quarterly' && (
                  <motion.span
                    layoutId="public-membership-tab-underline"
                    className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                    transition={{ type: 'spring', duration: 0.3 }}
                  />
                )}
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('annually')}
                className={cn(
                  'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                  billingPeriod === 'annually' ? 'text-white' : 'text-[#8E8E93] hover:text-white'
                )}
                aria-current={billingPeriod === 'annually' ? 'page' : undefined}
              >
                Annually
                {billingPeriod === 'annually' && (
                  <motion.span
                    layoutId="public-membership-tab-underline"
                    className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                    transition={{ type: 'spring', duration: 0.3 }}
                  />
                )}
              </button>
            </nav>
            <Link
              to="/pricing/compare"
              className="inline-flex tracking-wide uppercase items-center gap-1.5 py-2 px-4 border border-[#3A3A3C] rounded-br-none text-sm font-medium text-[#8E8E93] hover:text-[#F33363] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm"
            >
              <GitCompare className="h-3.5 w-3.5" />
              Compare plans
            </Link>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
            {MEMBERSHIP_PLANS.map((plan, index) => {
              const price = getPrice(plan);
              const savings = getSavings(plan);
              const emiAmount = getEmiAmount(plan);
              const majorFeatures =
                billingPeriod === 'quarterly' ? plan.quarterlyMajorFeatures : plan.annualMajorFeatures;
              const features =
                billingPeriod === 'quarterly' ? plan.quarterlyFeatures : plan.annualFeatures;
              const isEconomy = plan.id === 'economy';
              const isPremium = plan.id === 'premium';
              const isElite = plan.id === 'elite';

              let gradientClass = '';
              let blurClass = '';
              let iconBgClass = '';
              let accentColor = '';

              if (isEconomy) {
                gradientClass = 'bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-[#2C2C2E]/30';
                blurClass = 'bg-[#3A3A3C]/10';
                iconBgClass = 'bg-[#2C2C2E]';
                accentColor = 'text-[#8E8E93]';
              } else if (isPremium) {
                gradientClass = 'bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-amber-500/10';
                blurClass = 'bg-amber-500/5';
                iconBgClass = 'bg-amber-500/10';
                accentColor = 'text-amber-500';
              } else if (isElite) {
                gradientClass = 'bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-pink-500/10';
                blurClass = 'bg-pink-500/5';
                iconBgClass = 'bg-pink-500/10';
                accentColor = 'text-pink-400';
              }

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={cn(
                    'relative rounded-[2rem] border flex flex-col shadow-2xl',
                    gradientClass,
                    isElite ? 'border-[#3A3A3C]' : 'border-[#3A3A3C]/50 hover:border-[#3A3A3C]',
                    plan.isPopular && 'lg:scale-105'
                  )}
                >
                  {plan.isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 px-5 py-1.5 bg-gradient-to-r from-[#1C1C1E] via-[#1C1C1E] to-[#2C2C2E]/30 border border-[#1C1C1E] rounded-full text-xs font-black text-[#8E8E93] uppercase tracking-wider shadow-lg">
                      Most Popular
                    </div>
                  )}

                  <div className="relative overflow-hidden rounded-[2rem] flex flex-col flex-1">
                    <div
                      className={cn(
                        'absolute top-0 right-0 w-40 h-40 blur-3xl rounded-full -mr-20 -mt-20',
                        blurClass
                      )}
                    />

                    <div className="relative z-10 flex-1 min-h-0 flex flex-col">
                      {/* Plan Header */}
                      <div className="relative z-10 p-6 lg:p-8 pb-4">
                        <div className="flex items-start justify-between mb-6">
                          <div
                            className={cn(
                              'w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center',
                              iconBgClass
                            )}
                          >
                            <Rocket className={cn('h-7 w-7 lg:h-8 lg:w-8', accentColor)} />
                          </div>
                          <div className="text-right pl-2">
                            <div
                              className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-bold border',
                                isPremium
                                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                                  : isElite
                                    ? 'bg-pink-500/10 border-pink-500/30 text-pink-400'
                                    : 'bg-[#252527] border-[#3A3A3C] text-[#8E8E93]'
                              )}
                            >
                              {plan.starCoinsDiscount}
                            </div>
                          </div>
                        </div>

                        <h2 className="text-3xl lg:text-4xl font-black text-white mb-2 tracking-tight">
                          {plan.name}
                        </h2>
                        <p className="text-sm text-[#8E8E93] mb-6 font-medium">{plan.tagline}</p>

                        <div className="mb-4">
                          {savings && (
                            <span
                              className={cn(
                                'inline-block px-3 py-1.5 rounded-lg text-xs font-bold mb-3 border',
                                isPremium
                                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                                  : isElite
                                    ? 'bg-pink-500/10 border-pink-500/30 text-pink-400'
                                    : 'bg-[#252527] border-[#3A3A3C] text-[#8E8E93]'
                              )}
                            >
                              {savings}
                            </span>
                          )}
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="text-5xl lg:text-6xl font-black text-white tracking-tighter">
                              ₹{price.toLocaleString()}
                            </span>
                            {emiAmount != null && (
                              <span
                                className="inline-block py-1.5 pl-3 pr-5 text-xs font-semibold tracking-wide text-[#E5E5EA] bg-[#2C2C2E] shadow-sm"
                                style={{
                                  clipPath:
                                    'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
                                }}
                              >
                                EMI @ ₹{emiAmount.toLocaleString()}/mo
                              </span>
                            )}
                          </div>
                          {billingPeriod === 'annually' && (
                            <p className="text-xs text-[#8E8E93] mt-3 font-medium">
                              {plan.annualSavings} with an annual subscription!
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Major Features Grid */}
                      <div className="relative z-10 px-6 lg:px-8 pb-4">
                        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-3 gap-2 lg:gap-3">
                          {majorFeatures.map((feature, featureIndex) => {
                            const Icon = feature.icon;
                            const isMediaUpload = feature.label === 'Media Uploads';
                            let imageCount: string | null = null;
                            let videoCount: string | null = null;
                            if (isMediaUpload) {
                              const match = feature.value.match(
                                /(\d+)\s*Images?,\s*(\d+)\s*Videos?/i
                              );
                              if (match) {
                                imageCount = match[1];
                                videoCount = match[2];
                              }
                            }
                            return (
                              <div
                                key={featureIndex}
                                className={cn(
                                  'aspect-square rounded-xl lg:rounded-2xl p-2.5 lg:p-3 border transition-all flex flex-col items-center justify-center text-center',
                                  isPremium
                                    ? 'bg-amber-500/5 border-amber-500/5 hover:bg-amber-500/10'
                                    : isElite
                                      ? 'bg-pink-500/5 border-pink-500/5 hover:bg-pink-500/10'
                                      : 'bg-[#1C1C1E] border-[#3A3A3C]/30 hover:border-[#3A3A3C]'
                                )}
                              >
                                <div
                                  className={cn(
                                    'w-7 h-7 lg:w-9 lg:h-9 rounded-lg lg:rounded-xl flex items-center justify-center mb-1.5 lg:mb-2',
                                    isPremium ? 'bg-amber-500/10' : isElite ? 'bg-pink-500/10' : 'bg-[#2C2C2E]'
                                  )}
                                >
                                  <Icon
                                    className={cn(
                                      'h-3.5 w-3.5 lg:h-4 lg:w-4',
                                      isPremium
                                        ? 'text-amber-200'
                                        : isElite
                                          ? 'text-pink-200'
                                          : 'text-[#8E8E93]'
                                    )}
                                  />
                                </div>
                                <p className="text-[10px] lg:text-xs text-[#8E8E93] font-medium mb-0.5 lg:mb-1 line-clamp-1">
                                  {feature.label}
                                </p>
                                {isMediaUpload && imageCount && videoCount ? (
                                  <div className="flex items-center justify-center gap-3 text-[11px] lg:text-xs text-white font-bold">
                                    <span className="inline-flex items-center gap-1">
                                      <Image className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-[#E5E7EB]" />
                                      {imageCount}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Video className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-[#E5E7EB]" />
                                      {videoCount}
                                    </span>
                                  </div>
                                ) : (
                                  <p className="text-[11px] lg:text-xs text-white font-bold line-clamp-2">
                                    {feature.value}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Other Features List */}
                      <div className="relative z-10 flex-1 px-6 lg:px-8 pb-6">
                        <div className="space-y-2.5 max-h-[200px] overflow-y-auto pr-2">
                          {features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-start gap-2.5">
                              <div
                                className={cn(
                                  'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                                  isPremium ? 'bg-amber-500/20' : isElite ? 'bg-pink-500/20' : 'bg-[#3A3A3C]'
                                )}
                              >
                                <Check
                                  className={cn(
                                    'h-2.5 w-2.5',
                                    isPremium ? 'text-amber-400' : isElite ? 'text-pink-400' : 'text-[#8E8E93]'
                                  )}
                                />
                              </div>
                              <p className="text-xs text-[#8E8E93] leading-relaxed">{feature.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Footer: Preview + Sign up CTA */}
                      <div className="relative z-10 p-6 lg:p-8 pt-4 border-t border-[#3A3A3C]/50 space-y-3">
                        <button
                          onClick={() => window.open(plan.previewUrl, '_blank')}
                          className={cn(
                            'w-full flex items-center justify-center gap-2 text-sm font-medium transition-colors rounded-xl py-2',
                            isPremium
                              ? 'text-amber-400 hover:text-amber-300'
                              : isElite
                                ? 'text-pink-400 hover:text-pink-300'
                                : 'text-[#8E8E93] hover:text-white'
                          )}
                        >
                          Preview the {plan.name} Website
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        {/* <Link
                          to="/signup"
                          className="w-full flex items-center justify-center py-4 lg:py-4.5 text-base lg:text-lg font-bold rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58] text-white transition-colors"
                        >
                          Sign up to get this plan
                        </Link> */}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};
